import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, customerApi, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { useSettings, computeTotals } from "@/context/SettingsContext";
import Header from "@/components/Header";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { ShippingQuote } from "@/components/ShippingQuote";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Check, Banknote, Building2, CreditCard, Landmark } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Billing", "Delivery", "Shipping", "Review", "Payment"];
const emptyAddr = { line1: "", line2: "", city: "", region: "", postcode: "", country: "New Zealand" };

export default function CheckoutPage() {
  const { items, subtotal, clear } = useCart();
  const settings = useSettings();
  const { customer } = useCustomerAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [sameAsBilling, setSame] = useState(true);
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", notes: "",
    billing: { ...emptyAddr }, delivery: { ...emptyAddr }, payment_method: "bank_transfer",
  });
  const t0 = computeTotals(subtotal, settings);
  const [quote, setQuote] = useState(null);
  const [island, setIsland] = useState(null);
  const deliv = sameAsBilling ? form.billing : form.delivery;
  const qKey = JSON.stringify([deliv, items.map((i) => [i.id, i.qty]), island]);
  useEffect(() => {
    if (!items.length) return undefined;
    const h = setTimeout(() => {
      api.post("/shipping/quote", { items: items.map((i) => ({ product_id: i.id, qty: i.qty })), address: deliv,
        addy_id: deliv.addy_id || null, confirmed_island: island }).then((r) => setQuote(r.data)).catch(() => setQuote(null));
    }, 400);
    return () => clearTimeout(h);
  }, [qKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const pending = quote?.status === "quote_pending";
  const t = quote && quote.status !== "needs_address"
    ? { ...t0, subtotal: quote.subtotal, shipping: quote.shipping, total: quote.total, gstIncluded: quote.gst_included, free: quote.free_shipping && !pending }
    : t0;

  useEffect(() => {
    if (customer && typeof customer === "object") {
      setForm((f) => ({
        ...f,
        name: f.name || customer.name || "",
        email: f.email || customer.email || "",
        phone: f.phone || customer.phone || "",
        company: f.company || customer.company || "",
        billing: f.billing.line1 ? f.billing : { ...emptyAddr, ...(customer.saved_address || {}) },
      }));
    }
  }, [customer]);

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-background"><Header />
        <div className="max-w-xl mx-auto text-center py-24">
          <p className="text-muted-foreground">Your cart is empty.</p>
          <Button className="mt-4 bg-steel text-white" onClick={() => navigate("/")}>Continue Shopping</Button>
        </div>
      </div>
    );
  }

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (which, k, v) => setForm((f) => ({ ...f, [which]: { ...f[which], [k]: v } }));
  const fillAddr = (which) => (d) => setForm((f) => ({
    ...f,
    [which]: {
      line1: d.line1 || "", line2: d.line2 || "", city: d.city || "",
      region: d.region || "", postcode: d.postcode || "", country: d.country || "New Zealand", addy_id: d.addy_id || null,
    },
  }));

  const validateStep = () => {
    if (step === 0) {
      if (!form.name || !form.email) { toast.error("Enter your name and email"); return false; }
      if (!form.billing.line1 || !form.billing.city) { toast.error("Enter billing address"); return false; }
    }
    if (step === 1 && !sameAsBilling) {
      if (!form.delivery.line1 || !form.delivery.city) { toast.error("Enter delivery address"); return false; }
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    if (step === 2 && quote?.status === "needs_address") { toast.error("Please confirm your delivery island"); return; }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const placeOrder = async () => {
    setBusy(true);
    const delivery = sameAsBilling ? form.billing : form.delivery;
    try {
      const { data } = await customerApi.post("/checkout", {
        name: form.name, company: form.company, email: form.email, phone: form.phone,
        billing_address: form.billing, delivery_address: delivery,
        payment_method: form.payment_method, notes: form.notes,
        origin_url: window.location.origin, addy_id: delivery.addy_id || null, confirmed_island: island,
        items: items.map((i) => ({ product_id: i.id, qty: i.qty })),
      });
      if (data.redirect_url) {
        // Card / POLi: go to the gateway; cart is cleared on the success page.
        window.location.href = data.redirect_url;
        return;
      }
      clear();
      navigate(`/order/${data.order_number}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place order");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background"><Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-extrabold mb-6">Checkout</h1>

        {(!customer || typeof customer !== "object") && (
          <div data-testid="checkout-signin-banner" className="mb-6 flex items-center justify-between gap-3 flex-wrap rounded-xl border bg-amber-50/60 px-4 py-3">
            <span className="text-sm">Have an account? <strong>Sign in</strong> to prefill your details and track this order.</span>
            <Button variant="outline" size="sm" data-testid="checkout-signin-btn" onClick={() => navigate("/login")}>Sign in</Button>
          </div>
        )}
        {customer && typeof customer === "object" && (
          <div className="mb-6 rounded-xl border bg-emerald-50/60 px-4 py-3 text-sm" data-testid="checkout-signed-in">
            Signed in as <strong>{customer.email}</strong> — your saved details have been filled in. This order will be added to your account.
          </div>
        )}

        {/* stepper */}
        <div className="flex items-center gap-1 mb-8 overflow-x-auto">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center shrink-0">
              <div data-testid={`step-${label.toLowerCase()}`}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                  i === step ? "bg-accent text-steel" : i < step ? "bg-emerald-600 text-white" : "bg-slate-100 text-muted-foreground"}`}>
                <span className="grid place-items-center w-5 h-5 rounded-full bg-white/40 text-xs">
                  {i < step ? <Check size={12} /> : i + 1}</span>{label}
              </div>
              {i < STEPS.length - 1 && <div className="w-4 h-px bg-slate-300 mx-1" />}
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 space-y-5">
            {step === 0 && (
              <Card title="Billing Details">
                <Grid2>
                  <Field label="Full Name *" testid="bill-name" value={form.name} onChange={(v) => setField("name", v)} />
                  <Field label="Company" testid="bill-company" value={form.company} onChange={(v) => setField("company", v)} />
                  <Field label="Email *" testid="bill-email" type="email" autoComplete="email" value={form.email} onChange={(v) => setField("email", v)} />
                  <Field label="Phone" testid="bill-phone" type="tel" autoComplete="tel" value={form.phone} onChange={(v) => setField("phone", v)} />
                </Grid2>
                <AddressForm which="billing" prefix="bill" addr={form.billing} setAddr={setAddr} onFill={fillAddr("billing")} />
              </Card>
            )}
            {step === 1 && (
              <Card title="Delivery Address">
                <label className="flex items-center gap-2 text-sm mb-4">
                  <Checkbox data-testid="same-as-billing" checked={sameAsBilling} onCheckedChange={(c) => setSame(!!c)} />
                  Delivery address is the same as billing
                </label>
                {!sameAsBilling && <AddressForm which="delivery" prefix="del" addr={form.delivery} setAddr={setAddr} onFill={fillAddr("delivery")} />}
                {sameAsBilling && <p className="text-sm text-muted-foreground">Using billing address for delivery.</p>}
              </Card>
            )}
            {step === 2 && (
              <Card title="Shipping Options">
                <ShippingQuote q={quote} confirmed={island} onConfirm={setIsland} />
              </Card>
            )}
            {step === 3 && (
              <Card title="Review Your Order">
                <div className="space-y-3 text-sm">
                  {items.map((i) => (
                    <div key={i.id} className="flex justify-between border-b pb-2">
                      <span>{i.qty} × {i.name} <span className="font-mono text-muted-foreground">({i.sku})</span></span>
                      <span className="font-mono">{money(i.price * i.qty)}</span>
                    </div>
                  ))}
                  <div className="pt-2 text-muted-foreground">Deliver to: {form.name}, {(sameAsBilling ? form.billing : form.delivery).line1}, {(sameAsBilling ? form.billing : form.delivery).city}</div>
                  <div><Label>Order notes</Label><Textarea data-testid="order-notes" value={form.notes} onChange={(e) => setField("notes", e.target.value)} /></div>
                </div>
              </Card>
            )}
            {step === 4 && (
              <Card title="Payment Method">
                <div className="space-y-3">
                  <label data-testid="pay-bank-transfer" className="flex items-start gap-3 rounded-xl border-2 border-accent bg-amber-50/50 p-4 cursor-pointer">
                    <input type="radio" checked={form.payment_method === "bank_transfer"}
                      onChange={() => setField("payment_method", "bank_transfer")} className="mt-1" />
                    <Banknote className="text-accent shrink-0" />
                    <div>
                      <div className="font-semibold">Bank Transfer / Direct Bank Payment</div>
                      <div className="text-sm text-muted-foreground">You'll receive ACP's bank details and your payment reference after placing the order.</div>
                    </div>
                  </label>
                  {settings?.stripe_enabled && (
                    <label data-testid="pay-stripe" className="flex items-start gap-3 rounded-xl border p-4 cursor-pointer">
                      <input type="radio" checked={form.payment_method === "stripe"}
                        onChange={() => setField("payment_method", "stripe")} className="mt-1" />
                      <CreditCard className="shrink-0" />
                      <div><div className="font-semibold">Card Payment (Stripe)</div>
                        <div className="text-sm text-muted-foreground">Pay securely by credit/debit card in NZD.</div></div>
                    </label>
                  )}
                  {settings?.poli_enabled && (
                    <label data-testid="pay-poli" className="flex items-start gap-3 rounded-xl border p-4 cursor-pointer">
                      <input type="radio" checked={form.payment_method === "poli"}
                        onChange={() => setField("payment_method", "poli")} className="mt-1" />
                      <Landmark className="shrink-0" />
                      <div><div className="font-semibold">POLi Pay — Online Bank Payment</div>
                        <div className="text-sm text-muted-foreground">Pay directly from your NZ bank account via POLi.</div></div>
                    </label>
                  )}
                </div>
              </Card>
            )}

            <div className="flex justify-between">
              <Button variant="outline" onClick={back} disabled={step === 0} data-testid="checkout-back">Back</Button>
              {step < STEPS.length - 1 ? (
                <Button onClick={next} data-testid="checkout-next" className="bg-steel text-white hover:bg-steel-light">Continue</Button>
              ) : (
                <Button onClick={placeOrder} disabled={busy} data-testid="place-order-btn"
                  className="bg-accent text-steel hover:bg-amber-600 font-semibold px-8">
                  {busy ? "Placing order..." : "Place Order"}
                </Button>
              )}
            </div>
          </div>

          {/* summary */}
          <div className="lg:col-span-5">
            <div className="rounded-xl border bg-white p-6 sticky top-24 space-y-3">
              <h3 className="font-heading font-bold text-lg">Order Summary</h3>
              {items.map((i) => (
                <div key={i.id} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{i.qty} × {i.name}</span>
                  <span className="font-mono">{money(i.price * i.qty)}</span>
                </div>
              ))}
              <div className="border-t pt-3 space-y-2 text-sm">
                <div className="flex justify-between"><span>Subtotal (incl GST)</span><span className="font-mono" data-testid="sum-subtotal">{money(t.subtotal)}</span></div>
                <div className="flex justify-between"><span>Shipping</span><span className="font-mono" data-testid="sum-shipping">{quote?.status === "needs_address" ? "Confirm address" : t.free ? "FREE" : money(t.shipping)}{pending && " + freight quote"}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>GST (15%) included</span><span className="font-mono" data-testid="sum-gst">{money(t.gstIncluded)}</span></div>
                <div className="flex justify-between font-bold text-lg border-t pt-2"><span>Total (NZD)</span><span className="font-mono" data-testid="sum-total">{money(t.total)}</span></div>
              </div>
              {pending && <p className="text-xs text-red-700" data-testid="sum-freight-pending">Freight is not included yet — we'll email a quote for you to approve before dispatch. This total is not final.</p>}
              {!t.free && t0.remaining > 0 && <p className="text-xs text-accent">Add {money(t0.remaining)} more to unlock FREE NZ shipping.</p>}
            </div>
          </div>
        </div>
      </div>
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t bg-white/95 backdrop-blur px-4 py-3 flex items-center gap-3" data-testid="checkout-mobile-bar">
        <div className="min-w-0"><div className="text-xs text-muted-foreground">Total (NZD)</div><div className="font-mono font-bold">{money(t.total)}</div></div>
        {step < STEPS.length - 1
          ? <Button onClick={next} className="ml-auto h-11 bg-steel text-white" data-testid="checkout-mobile-next">Continue</Button>
          : <Button onClick={placeOrder} disabled={busy} className="ml-auto h-11 bg-accent text-steel font-semibold" data-testid="checkout-mobile-place">{busy ? "Placing…" : "Place Order"}</Button>}
      </div>
      <div className="h-20 lg:hidden" />
    </div>
  );
}

function Card({ title, children }) {
  return <div className="rounded-xl border bg-white p-6"><h2 className="font-heading font-bold text-xl mb-4">{title}</h2>{children}</div>;
}
function Grid2({ children }) { return <div className="grid sm:grid-cols-2 gap-4">{children}</div>; }
function Field({ label, value, onChange, testid, ...rest }) {
  return <div><Label htmlFor={testid}>{label}</Label><Input id={testid} data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} {...rest} /></div>;
}
function AddressForm({ prefix, addr, setAddr, which, onFill }) {
  return (
    <div className="mt-4 grid sm:grid-cols-2 gap-4">
      <AddressAutocomplete prefix={prefix} onSelect={onFill} />
      <div className="sm:col-span-2"><Label>Address Line 1 *</Label><Input data-testid={`${prefix}-line1`} value={addr.line1} onChange={(e) => setAddr(which, "line1", e.target.value)} /></div>
      <div className="sm:col-span-2"><Label>Address Line 2</Label><Input data-testid={`${prefix}-line2`} value={addr.line2} onChange={(e) => setAddr(which, "line2", e.target.value)} /></div>
      <div><Label>City *</Label><Input data-testid={`${prefix}-city`} value={addr.city} onChange={(e) => setAddr(which, "city", e.target.value)} /></div>
      <div><Label>Region</Label><Input data-testid={`${prefix}-region`} value={addr.region} onChange={(e) => setAddr(which, "region", e.target.value)} /></div>
      <div><Label>Postcode</Label><Input data-testid={`${prefix}-postcode`} inputMode="numeric" autoComplete="postal-code" maxLength={4} value={addr.postcode} onChange={(e) => setAddr(which, "postcode", e.target.value)} /></div>
      <div><Label>Country</Label><Input data-testid={`${prefix}-country`} value={addr.country} onChange={(e) => setAddr(which, "country", e.target.value)} /></div>
    </div>
  );
}
